import SeliarisLogo from "@/assets/integrations/seliaris.png";
import { ButtonLoading } from "@/components/button-loading";
import PageSkeleton from "@/components/page-skeleton";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast, useToastError } from "@/components/ui/use-toast";
import { useSeliarisIntegration } from "@/contexts/seliaris-integration-provider";
import { IntegrationCrmSeliarisPushResultDTO } from "@shaarp/common";
import { TeamUserRolesEnum } from "@/enums/team-user-role";
import useAccess from "@/hooks/useAccess";
import { useApi } from "@/hooks/useApi";
import { useTranslation } from "@/hooks/useTranslation";
import { convertDateToDistanceNow } from "@/lib/date";
import {
  getSeliarisErrorFields,
  SELIARIS_ERROR_MESSAGE_KEY,
  getSeliarisErrorMessageKey,
} from "@/lib/seliaris-errors";
import {
  AlertTriangle,
  ArrowLeft,
  CheckCircle,
  KeyRound,
  LogOut,
  RefreshCw,
  Zap,
} from "lucide-react";
import { ReactNode, useState } from "react";
import { useNavigate } from "react-router-dom";

/** Says why the connect button is out of reach: no Pro licence, or no role. */
const RestrictionBadge = ({
  icon,
  label,
}: {
  icon: ReactNode;
  label: string;
}) => (
  <Badge variant="secondary" className="flex gap-1.5 items-center px-3 py-1">
    {icon}
    <span className="text-xs">{label}</span>
  </Badge>
);

export default function IntegrationSeliarisPage() {
  const api = useApi();
  const toastError = useToastError();
  const { toast } = useToast();
  const { t, currentLanguage } = useTranslation();
  const navigate = useNavigate();
  const { hasAccess } = useAccess();
  const {
    seliarisIntegration,
    isLoading: isStatusLoading,
    hasLoadFailed,
    refetch,
  } = useSeliarisIntegration();

  const [apiKey, setApiKey] = useState("");
  const [isConnecting, setIsConnecting] = useState(false);
  const [isDisconnecting, setIsDisconnecting] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [showDisconnectDialog, setShowDisconnectDialog] = useState(false);

  const hasCorrectLicence = hasAccess({
    requiredLicences: ["professional"],
    licenceActiveState: true,
  });
  const hasRoleAccess = hasAccess({
    requiredRoles: [TeamUserRolesEnum.MANAGER, TeamUserRolesEnum.ADMIN],
  });
  const canConnect = hasCorrectLicence && hasRoleAccess;

  /** Shows the message matching the API's error code, or the generic toast. */
  const reportError = (error: unknown) => {
    const messageKey = getSeliarisErrorMessageKey(error);
    if (!messageKey) {
      toastError();
      return;
    }
    toast({
      variant: "destructive",
      title: t("team.integrations.seliaris.errors.title"),
      // The custom fields a refusal named are interpolated when it carried
      // any; every other message ignores the parameter.
      description: t(messageKey, { fields: getSeliarisErrorFields(error) }),
    });
  };

  const handleConnect = async () => {
    try {
      setIsConnecting(true);
      await api.post("/integration/crm/seliaris", { apiKey });
      // Cleared as soon as it has been sent: the key is never displayed again,
      // and the status only ever carries its last four characters.
      setApiKey("");
      await refetch();
      toast({
        title: t("team.integrations.seliaris.success.title"),
        description: t("team.integrations.seliaris.success.description"),
      });
    } catch (error) {
      reportError(error);
    } finally {
      setIsConnecting(false);
    }
  };

  /**
   * Reports what the cycle actually did.
   *
   * A cycle that stopped on a refused key, or failed every one of its records,
   * still answers 200 — the API's job was to run it, and it did. Announcing
   * that as a success is how a dead sync goes unnoticed for weeks.
   */
  const reportSyncResult = (result: IntegrationCrmSeliarisPushResultDTO) => {
    const succeeded = result.companiesSucceeded + result.contactsSucceeded;
    const failed = result.companiesFailed + result.contactsFailed;

    if (result.stoppedErrorCode) {
      toast({
        variant: "destructive",
        title: t("team.integrations.seliaris.sync.stopped.title"),
        description: t(SELIARIS_ERROR_MESSAGE_KEY[result.stoppedErrorCode], {
          fields: null,
        }),
      });
      return;
    }

    if (failed > 0) {
      toast({
        variant: "warning",
        title: t("team.integrations.seliaris.sync.partial.title"),
        description: t("team.integrations.seliaris.sync.partial.description", {
          succeeded,
          failed,
        }),
      });
      return;
    }

    toast({
      title: t("team.integrations.seliaris.sync.success.title"),
      // Not `count`: that name switches i18next to its plural machinery, and
      // these strings carry their own "(s)".
      description: t("team.integrations.seliaris.sync.success.description", {
        records: succeeded,
      }),
    });
  };

  const handleSync = async () => {
    try {
      setIsSyncing(true);
      const { data } = await api.post<IntegrationCrmSeliarisPushResultDTO>(
        "/integration/crm/seliaris/sync",
      );
      await refetch();
      reportSyncResult(data);
    } catch (error) {
      reportError(error);
    } finally {
      setIsSyncing(false);
    }
  };

  const confirmDisconnect = async () => {
    try {
      setIsDisconnecting(true);
      await api.delete("/integration/crm/seliaris");
      await refetch();
      toast({
        title: t("team.integrations.seliaris.disconnect.success.title"),
        description: t(
          "team.integrations.seliaris.disconnect.success.description",
        ),
      });
    } catch (error) {
      reportError(error);
    } finally {
      setIsDisconnecting(false);
      setShowDisconnectDialog(false);
    }
  };

  if (isStatusLoading) {
    return <PageSkeleton className="p-4" />;
  }

  const isActive = !!seliarisIntegration?.isActive;
  // The status could not be read at all: whether this team is connected is
  // unknown, so neither the key form nor the connected panel is shown — either
  // would be a claim the page cannot back up.
  const isStatusUnknown = hasLoadFailed && !seliarisIntegration;
  /**
   * Seliaris refused the stored key, as opposed to refusing writes on a
   * read-only account — both flip `isAvailable`, but only this one is fixed by
   * pasting a new key, and only this one makes a sync cycle pointless.
   */
  const isKeyRejected =
    !isStatusUnknown &&
    seliarisIntegration?.isAvailable === false &&
    !seliarisIntegration?.isReadOnly;

  return (
    <div className="mt-8">
      <div className="flex items-center gap-4 mb-6">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate("/integrations")}
          className="flex items-center gap-2"
        >
          <ArrowLeft className="w-4 h-4" />
          {t("common.back")}
        </Button>
      </div>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between pb-4 space-y-0">
          <div className="flex items-center gap-3">
            <img src={SeliarisLogo} alt="Seliaris" className="w-6 h-6" />
            <CardTitle>{t("team.integrations.seliaris.title")}</CardTitle>
            {isStatusUnknown ? (
              <Badge variant="outline" className="text-xs">
                {t("team.integrations.seliaris.badge.unknown")}
              </Badge>
            ) : isActive ? (
              <Badge variant="success" className="text-xs">
                {t("team.integrations.seliaris.badge.connected")}
              </Badge>
            ) : (
              <Badge variant="outline" className="text-xs">
                {t("team.integrations.seliaris.badge.notConnected")}
              </Badge>
            )}
          </div>
          {isActive && (
            <div className="flex items-center gap-3">
              {seliarisIntegration?.lastSyncAt && (
                <span className="text-sm text-muted-foreground">
                  {t("team.integrations.seliaris.sync.lastSync")}{" "}
                  {convertDateToDistanceNow(
                    new Date(seliarisIntegration.lastSyncAt),
                    { languageOverride: currentLanguage },
                  )}
                </span>
              )}
              <Button
                variant="outline"
                size="sm"
                onClick={handleSync}
                // A rejected key makes the API refuse the cycle outright, so an
                // enabled button could only ever produce an error toast the
                // reconnection banner right below has already explained.
                //
                // Same condition as that banner, and deliberately not plain
                // `isAvailable`: a read-only account also reads as unavailable,
                // yet the customer clears it on their side and then needs this
                // button to confirm it — disabling it there would trap them.
                disabled={isSyncing || isKeyRejected}
                className="flex items-center gap-2"
              >
                <RefreshCw
                  className={`w-4 h-4 ${isSyncing ? "animate-spin" : ""}`}
                />
                {isSyncing
                  ? t("team.integrations.seliaris.sync.buttonLoading")
                  : t("team.integrations.seliaris.sync.button")}
              </Button>
            </div>
          )}
        </CardHeader>

        <CardContent className="space-y-4">
          <Alert variant="warning">
            <AlertTitle>
              {t("team.integrations.seliaris.beta.title")}
            </AlertTitle>
            <AlertDescription>
              {t("team.integrations.seliaris.beta.description")}
            </AlertDescription>
          </Alert>

          {/*
            Seliaris has no endpoint to create a custom field definition, so the
            back link can only work if the customer creates the field first.
            Stating it here is the difference between a working link and a
            silently missing one.
          */}
          <Alert>
            <AlertTitle>
              {t("team.integrations.seliaris.prerequisite.title")}
            </AlertTitle>
            <AlertDescription>
              {t("team.integrations.seliaris.prerequisite.description")}
            </AlertDescription>
          </Alert>

          {isStatusUnknown && (
            <Alert variant="destructive">
              <AlertTriangle className="w-4 h-4" />
              <AlertTitle>
                {t("team.integrations.seliaris.statusUnavailable.title")}
              </AlertTitle>
              <AlertDescription className="flex flex-col items-start gap-3">
                {t("team.integrations.seliaris.statusUnavailable.description")}
                <Button variant="outline" size="sm" onClick={refetch}>
                  {t("team.integrations.seliaris.statusUnavailable.retry")}
                </Button>
              </AlertDescription>
            </Alert>
          )}

          {isKeyRejected && (
            <Alert variant="destructive">
              <AlertTriangle className="w-4 h-4" />
              <AlertTitle>
                {t("team.integrations.seliaris.keyRejected.title")}
              </AlertTitle>
              <AlertDescription>
                {t("team.integrations.seliaris.keyRejected.description")}
              </AlertDescription>
            </Alert>
          )}

          {seliarisIntegration?.isReadOnly && (
            <Alert variant="destructive">
              <AlertTriangle className="w-4 h-4" />
              <AlertTitle>
                {t("team.integrations.seliaris.readOnly.title")}
              </AlertTitle>
              <AlertDescription>
                {t("team.integrations.seliaris.readOnly.description")}
              </AlertDescription>
            </Alert>
          )}

          {seliarisIntegration?.isCustomFieldMissing && (
            <Alert variant="warning">
              <AlertTriangle className="w-4 h-4" />
              <AlertTitle>
                {t("team.integrations.seliaris.customFieldMissing.title")}
              </AlertTitle>
              <AlertDescription>
                {t("team.integrations.seliaris.customFieldMissing.description")}
              </AlertDescription>
            </Alert>
          )}

          {!isActive && !isStatusUnknown && (
            <>
              <p className="text-muted-foreground">
                {t("team.integrations.seliaris.status.description")}
              </p>

              <div className="space-y-2 max-w-md">
                <Label htmlFor="seliaris-api-key">
                  {t("team.integrations.seliaris.form.apiKeyLabel")}
                </Label>
                <Input
                  id="seliaris-api-key"
                  type="password"
                  autoComplete="off"
                  value={apiKey}
                  onChange={(event) => setApiKey(event.target.value)}
                  placeholder={t(
                    "team.integrations.seliaris.form.apiKeyPlaceholder",
                  )}
                  disabled={!canConnect || isConnecting}
                />
                <p className="text-xs text-muted-foreground">
                  {t("team.integrations.seliaris.form.apiKeyHelp")}
                </p>
              </div>

              <div className="flex items-center gap-3">
                <ButtonLoading
                  isLoading={isConnecting}
                  onClick={handleConnect}
                  disabled={!canConnect || apiKey.trim().length === 0}
                  className="flex items-center gap-2"
                >
                  <KeyRound className="w-4 h-4" />
                  {t("team.integrations.seliaris.connect")}
                </ButtonLoading>
                {!hasCorrectLicence && (
                  <RestrictionBadge
                    icon={<Zap className="w-3.5 h-3.5 text-purple-500" />}
                    label={t(
                      "team.integrations.seliaris.connectTooltip.proOnly",
                    )}
                  />
                )}
                {hasCorrectLicence && !hasRoleAccess && (
                  <RestrictionBadge
                    icon={
                      <AlertTriangle className="w-3.5 h-3.5 text-orange-500" />
                    }
                    label={t(
                      "team.integrations.seliaris.connectTooltip.memberRestriction",
                    )}
                  />
                )}
              </div>
            </>
          )}

          {isActive && (
            <div className="space-y-4">
              <div className="flex items-start gap-4 p-4 border border-green-200 rounded-lg bg-green-50 dark:bg-green-950 dark:border-green-800">
                <CheckCircle className="w-5 h-5 text-success mt-0.5" />
                <div className="flex-1">
                  <h3 className="mb-1 font-medium text-green-900 dark:text-green-100">
                    {t("team.integrations.seliaris.disconnect.connectedTitle")}
                  </h3>
                  <p className="text-sm text-green-700 dark:text-green-300">
                    {t(
                      "team.integrations.seliaris.disconnect.connectedDescription",
                    )}
                  </p>
                  {seliarisIntegration?.apiKeyLast4 && (
                    <p className="mt-2 text-sm font-mono text-green-700 dark:text-green-300">
                      {`••••••••${seliarisIntegration.apiKeyLast4}`}
                    </p>
                  )}
                </div>
              </div>
              <div className="flex justify-end">
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() => setShowDisconnectDialog(true)}
                  disabled={isDisconnecting || !hasRoleAccess}
                  className="flex items-center gap-2"
                >
                  <LogOut className="w-4 h-4" />
                  {isDisconnecting
                    ? t("team.integrations.seliaris.disconnect.buttonLoading")
                    : t("team.integrations.seliaris.disconnect.button")}
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <AlertDialog
        open={showDisconnectDialog}
        onOpenChange={setShowDisconnectDialog}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t("team.integrations.seliaris.disconnect.confirmDialog.title")}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t(
                "team.integrations.seliaris.disconnect.confirmDialog.description",
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>
              {t("team.integrations.seliaris.disconnect.confirmDialog.cancel")}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDisconnect}
              disabled={isDisconnecting}
              className="bg-destructive hover:bg-destructive/90"
            >
              {isDisconnecting
                ? t("team.integrations.seliaris.disconnect.buttonLoading")
                : t(
                    "team.integrations.seliaris.disconnect.confirmDialog.confirm",
                  )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
